/**
 * Copy for the jargon tooltips across the app.
 *
 * Source: the tooltip candidate list in the FinCreator UX Audit (12 Sep 2026).
 * Definitions that depend on the model or on how a number is computed were
 * rewritten to match the backend; the comment above each cites where. Entries
 * marked "Not in the audit" are new copy.
 *
 * Where the audit quoted an example value ("0.0713", "10%"), the
 * copy takes the live value instead. Thresholds quoted as literals mirror
 * backend config and must move with it.
 */

const asPercent = (fraction: number) => {
  const pct = fraction * 100
  return `${pct.toFixed(pct < 1 ? 2 : 1)}%`
}

// ---- Signals ----

export const SIGNALS_GLOSSARY = {
  // Backend core/config.py: GROWTH_PHASE1_MIN_SCORE / GROWTH_PHASE2_MIN_SCORE /
  // GROWTH_PHASE3_MIN_CONFIDENCE (conservative gates in services/trading.py),
  // GROWTH_*_TARGET_MULTIPLIER, and the two *_RECOMMENDED_RISK_PCT values.
  tradingMode:
    'Risk mode. Growth accepts slightly weaker setups (Phase 1 ≥ 0.45, Phase 2 ≥ 0.55, confidence ≥ 0.65, vs 0.50 / 0.60 / 0.70 in Conservative), sets wider take-profit targets, and recommends risking 2.5% of capital per trade instead of 1%.',

  portfolioHeat: (cap?: string) =>
    "Total capital you'd lose if every open position hit its stop at once." + (cap ? ` Your cap is ${cap}.` : ''),

  // Backend ml/phase3_ensemble.py, _calculate_overall_confidence: Phase 1
  // score, Phase 2 (ML) confidence and regime confidence weighted 25/45/30,
  // blended 60% linear + 40% log-odds, minus a turbulence penalty.
  confidence:
    "The model's overall conviction in this setup: the Phase 1 score, Phase 2 score and market-regime confidence blended with weights of 25%, 45% and 30%, then reduced when markets are turbulent.",

  // Backend ml/phase1_analyzer.py, _analyze_trend_alignment (EMA 9/21, 50, 200)
  // and _calculate_phase1_score (weights 0.35 / 0.30 / 0.35).
  phase1:
    "Technical setup score: trend alignment across the 9/21, 50 and 200-period EMAs (35%), rising volume (30%) and momentum in the trade's direction (35%).",

  // Backend ml/phase2_validator.py, _calculate_phase2_score (0.7 / 0.3) and
  // _fallback_validation (Phase 1 x 0.9); training starts at
  // PHASE2_MIN_TRAINING_SAMPLES usable rows (200, code default and production
  // alike), the label is gross R > 0 (before fees, decided 2026-10-09), and
  // train_model deploys the model only if it beats the fallback and the base
  // rate on the newest held-out signals and the signals it keeps average more
  // R (before fees) than all of them.
  phase2:
    "Machine-learning check: a gradient-boosting model's probability that the setup closes in profit before fees, the same win the record counts (70%), blended with the Phase 1 score (30%). The model trains once 200 closed signals are usable and is used only if, on the newest signals, it predicts better than both the fallback and the plain base rate and the signals it keeps average more R before fees than all of them; otherwise this is the Phase 1 score × 0.9.",

  // Backend ml/phase3_ensemble.py, _ensemble_vote (0.3 / 0.5 / 0.2 with a
  // per-regime multiplier) and _refine_trade_levels.
  phase3:
    'Ensemble vote: Phase 1 (30%), Phase 2 (50%) and a weighting for the current market regime (20%). This phase also sets the stop and target distances for the regime and trading mode.',

  // Backend ml/phase2_validator.py, _calculate_ev; until Phase 2 trains,
  // win_probability is phase1_score x 0.9 (Phase 2's fallback path). Signals
  // publish only at that probability >= 0.60 (0.55 Growth), so the figure
  // could never read below about +0.56 (+0.43): it is not an expectation, and
  // no card shows it.
  ev: "Expected value isn't shown because, until Phase 2 has a trained model, the win probability is the Phase 1 score × 0.9, not a measured probability.",

  // Backend services/leverage_calculator.py: "weak" below 1.5 (-0.5x),
  // "strong" at 2.0 or above (+0.5x), within the leverage ceiling.
  rewardRisk: (ratio: number, quality?: string) =>
    `Reward-to-risk: you're risking $1 to make $${ratio.toFixed(2)}.` +
    (quality === 'weak'
      ? ' Labelled weak below 1.5:1, which trims the leverage recommendation by 0.5×.'
      : quality === 'strong'
      ? ' Labelled strong at 2:1 or better, which adds 0.5× to the leverage recommendation, within its ceiling.'
      : '') +
    ' The R:R beside the levels is before fees; the R:R on the leverage line takes off an estimated round-trip cost (fees plus slippage), so it reads lower.',

  // Backend ml/phase3_ensemble.py, _simple_regime_detection: 20-bar volatility
  // and trend strength against fixed thresholds; confidence from the margin
  // (0.5-0.95), plus a persistence bonus, minus a turbulence penalty.
  regimeWithConfidence: (regime: string, confidence: string) =>
    `The model classified the market as ${regime.toLowerCase()} from the last 20 candles' volatility and trend strength. ${confidence} reflects how clearly those readings cleared the threshold, raised when the regime has persisted and lowered in turbulent markets.`,

  // Backend services/leverage_calculator.py: regime confidence < 0.80 -> -0.5x.
  regimeConfidence:
    "How certain the model is about which market state we're in — not how certain it is about the trade. Below 80% it trims the leverage recommendation by 0.5×.",

  // Backend services/leverage_calculator.py: volatility is ATR / close, with
  // ATR over 14 candles (ml/indicators.py); low < 0.008, moderate < 0.015.
  volatility: (value: number) =>
    `ATR (the average candle range over the last 14 candles) as a fraction of price: ${value} means a typical candle moves about ${asPercent(value)}. Low below 0.008, moderate below 0.015, elevated above; higher volatility scales leverage down.`,

  // Regime ceilings in leverage_calculator.py: calm 5x, trending 4x, ranging 2x,
  // volatile 1x; capped at 2x by the leverage gate (EV below 0.2).
  leverageCeiling: 'Maximum leverage the model will endorse here. 1× means spot only, no borrowing.',

  // Backend services/leverage_calculator.py: the card shows this whenever the
  // recommendation is 1x, from any of these layers.
  leverageNotRecommended:
    "The model recommends no leverage (1×) for this setup. The model's leverage gate can block it outright; otherwise the regime's ceiling (1× in volatile markets), high volatility, a weak R:R or low regime confidence brought it down to 1×. The rationale below shows which.",

  // Backend settings.LEVERAGE_SUGGESTIONS_ENABLED (off): every card's leverage
  // box shows only "No leverage suggested while the signal engine is under
  // evaluation.", old signals included.
  leverageOff:
    "FinCreator suggests no leverage while the signal engine is under evaluation: it has no proven edge, and leverage multiplies losses as well as gains. P&L is shown at 1×.",

  noLiquidationRisk:
    "Without leverage there's no forced closure — your worst case is the stop loss, not a wipeout.",

  riskPct: 'Share of total capital at stake on this one position if the stop is hit.',

  // Backend services/signal_exit.decide_exit: the rule the record scores.
  exitManagement:
    'The whole position is held until the first of three things: the target is hit, the stop is hit, or the signal expires and closes at the market price. The record checks the exchange price every 5 minutes and closes at that price, so an exit can land a little past the target or stop. This is exactly how the track record scores every signal. On a closed signal the same levels are shown as history, not as something to act on now.',

  // Backend services/trading.py: _compute_signal_expiry (SIGNAL_EXPIRY_CANDLES
  // = 8) and update_signal_status, which (from 2026-09-19) closes an expired
  // signal at the exchange's last price with exit_reason "expired" and scores
  // it by realised P&L like any other close.
  expiry:
    "A signal stays live for 8 candles of its timeframe: 32 hours on 4h, 8 days on 1d (and 8 hours on 1h, which is currently suspended). If neither target nor stop is hit by then, it closes at the market price and counts toward the record as a win, a loss or break-even.",

  statusActive: 'Signal is live and within its validity window.',
  statusClosed: 'Signal finished: it hit its target or its stop, or expired and closed at the market price. Every close counts toward the record.',
  statusCancelled:
    'Withdrawn with no result: expiries from before 2026-09-19 (cancelled back then) and duplicates cleaned up. Not scored.',

  regimeTag: 'Tag for the market regime this signal was generated under.',
  sinceEntry: 'Price movement since the signal was published — unrealized, and not a closed result.',

  scanAll:
    'Runs the model across every tracked pair and active timeframe, generating new signals where setups qualify. It lives on the admin Settings > Engine page, not on Signals. Every creation is checked against the portfolio-heat and open-position caps, so a scan skips a coin that would breach them rather than opening past the limit.',
  scanScope:
    'Scan scope: every tracked pair is checked on every active timeframe each run. The engine holds at most one open signal per coin per direction, whatever the timeframe it was found on.',
  retrainMl:
    'Rebuilds the prediction model on the latest closed-trade outcomes. Slow, and changes future signal scoring. Admin-only, on the Settings > Engine page.',
  analyze:
    'Runs the model against one symbol and timeframe on demand, instead of the full scan. Admin-only, on the Settings > Engine page.',
  timeframe:
    'Candle size the analysis runs on: 4h or 1d. Longer timeframes mean fewer, slower, larger moves. 1h is suspended, so the scan skips it and Analyze refuses it.',

  // Not in the audit. Backend core/config.py DISABLED_TIMEFRAMES, enforced in
  // tasks/signal_tasks.py and api/signals.py analyze.
  timeframeSuspended:
    '1h signals are suspended. The scan skips 1h, and Analyze on 1h runs nothing and says so. That is not a no-trade verdict.',

  // Not in the audit. Backend core/config.py TRADING_SYMBOLS (49 pairs) and
  // EXCLUDED_SYMBOLS; quotes come from Kraken (services/exchange_factory.py).
  universe:
    '49 crypto pairs on Kraken, quoted in USD: the original 20 (BTC, ETH, SOL and other large caps) plus majors such as DOGE, XRP and LTC, privacy coins, DeFi, AI coins and two established memecoins (PEPE, SHIB). Added coins needed a median $0.33M+ of daily Kraken volume and 200 days of daily history. No stocks, forex or commodities.',

  // Not in the audit. Backend core/config.py SCAN_MIN_DAILY_VOLUME_USD and
  // SCAN_LIQUIDITY_LOOKBACK_DAYS.
  liquidityFloor:
    'A coin is skipped on any scan where its median daily traded value on Kraken over the last 30 days is under $100,000. Thin order books print gappy candles that make stops meaningless and are easy to push around, so a coin that dries up stops being traded instead of producing bad signals.',

  // Not in the audit. Frontend utils/marketPrice.ts priceDecimals.
  microPrice:
    'Prices show as many decimals as their size needs: cents from $1 up, four decimals from $0.01, and four significant figures below that, so SHIB or PEPE at a few millionths of a dollar still shows entry, stop and target as different numbers.',

  // Not in the audit. ATR(14) is the unit Phase 3 sizes stops and targets in
  // (ml/phase3_ensemble.py _refine_trade_levels) and the unit the paper
  // strategy trails its stop in (3 x ATR, PAPER_GLOSSARY.trailingStop).
  atr:
    "Average True Range: a coin's typical move over the last 14 candles, measured in its own price rather than as a percentage. An ATR of $2 on a $100 coin means it usually travels about $2 a candle. Stops and targets are set in multiples of it, so a jumpy coin gets a wider stop than a quiet one instead of both getting the same fixed percentage.",

  // Not in the audit. The engine's standing evaluation status: backtesting
  // found no edge (components/trackRecord/recordSource.tsx), and
  // the full live record is published on /track-record.
  engineStatus:
    "The signal engine is under evaluation, not a finished product, and has not been shown to have an edge. Its whole live record is on the Track Record page: every closed signal is listed, wins and losses, and any signal excluded from the figures is shown with its reason, so the evaluation can be watched rather than taken on trust. It has not been shown to make money, and nothing here is financial advice.",

  // Not in the audit. Backend services/trading.py short gates (3-of-3 EMA
  // unanimity, SHORT_PHASE1_MIN_SCORE 0.55, SHORT_MIN_CONFIDENCE 0.74) and
  // short_regime_blocked(): volatile always, trending only while the trend is up.
  shortSignals:
    "Short signals clear four gates a long does not: all three EMAs must agree, a higher Phase 1 floor (0.55), a higher confidence floor (0.74), and no shorts at all in a volatile regime or while the trend is up. The long/short split on the Track Record shows how each side has actually done. A short signal is the least evidenced thing the engine produces, and the app does not put shorting forward as a strategy.",

  // Not in the audit. trading_signals.exit_reason, added in migration
  // a3b4c5d6e7f8; "expired" closes at the exchange's last price.
  exitReason:
    'Why a closed signal ended: it reached its target (a win), it hit its stop (a loss), or it expired and was closed at the market price for whatever it was worth. All three count toward the record.',

  // Not in the audit. Frontend pages/Signals.tsx status ribbon; the engine
  // console moved to the admin Settings > Engine page.
  statusRibbon:
    'Display-only strip on Signals showing the scanner is alive: when the last scan ran, how many symbols it covers, and roughly when the next one is due (an hour after the last). The controls that actually run a scan live on the admin Settings > Engine page.',
}

// ---- Dashboard ----

export const DASHBOARD_GLOSSARY = {
  portfolioHeatOverLimit: (cap: string) =>
    `Combined risk across open positions has exceeded your ${cap} ceiling — close or reduce before adding more.`,

  // Backend services/signal_summary_service.py, phase2_progress: the Phase 2
  // model first trains when PHASE2_MIN_TRAINING_SAMPLES usable rows exist
  // (collect_phase2_training_rows); until then Phase 2 uses the Phase 1 fallback.
  phase2Progress: (threshold: number) =>
    `Closed signals Phase 2 can train on: each needs its stored features, a result before fees (a measurable R) and its context (direction, timeframe, stop distance, regime). Until ${threshold} are usable, Phase 2 scores are estimated from Phase 1; at ${threshold} the model trains on their outcomes before fees.`,

  // Track record panel, Paper: backend paper_engine.account_stats profit_loss_pct.
  paperReturn:
    "The paper account's change from its $10,000 start, after the 0.25% fee on every entry and exit. Open positions are valued at the latest daily close, so it moves before trades close. Pretend money on real prices.",

  paperProgress: (evalDays: number, minTrades: number) =>
    `Days elapsed in the ~${evalDays}-day forward test. Its kill rule is judged once ${minTrades} trades have closed, so an early record is a small sample.`,

  winRateEmpty: "No closed trades yet, so there's nothing to compute. Not zero — undefined.",

  // Not in the audit. Frontend utils/winRate.ts MIN_WIN_RATE_SAMPLE, mirroring
  // backend services/track_record_service.py MIN_WIN_RATE_SAMPLE.
  winRateSmallSample: (closed: number, minimum: number) =>
    `Only ${closed} closed so far. A win rate is shown once ${minimum} have closed; fewer is too small a sample to mean anything.`,

  directionSplit: (openLong: number, openShort: number) =>
    'Directional split of open signals.' +
    (openLong > 0 && openShort === 0 ? ' All current positions bet on price rising.' : ''),

  lastScanNone: "No completed scan has been logged, so signal freshness can't be confirmed.",

  // Backend api/signals.py get_equity_curve: sum_of_returns_pct (and its
  // same-valued alias cumulative_pnl_pct), a running sum of
  // profit_loss_percent. Labelled "Sum of trade returns" in the UI since
  // 2026-09-20 because a sum of percentages is not an account return.
  cumulativePnl:
    'Sum of trade returns: every closed signal’s percentage return added together in the order they closed. It is unsized and not compounded, so it is not an account balance and it can fall past −100% while an account that risked a fixed slice per trade still has money in it. Read it as a tally, not a balance. The number that actually means something is the average R per trade.',

  // Backend services/signal_summary_service.py r_multiple: the gross
  // move / |entry - stop|. Before fees: nothing a user sees assumes a fee.
  avgR:
    'Average realised result per closed signal, in units of the risk it was opened with, before trading fees. +1R made what the stop risked; −1R lost it. Unlike the summed returns it does not depend on position size. Fees depend on your exchange and tier, so none is assumed here: the fee calculator shows what your fee does to it. Read it with the interval beneath it — a mean with no interval says nothing about whether the edge is real.',
}

// ---- Track Record ----

export const TRACK_RECORD_GLOSSARY = {
  // Not in the audit. GET /api/track-record/summary closed_all / closed_long /
  // closed_short (services/signal_summary_service.py _direction_record).
  directionSplit:
    'The same closed signals, grouped by direction. Every figure is computed exactly as the headline is, just over longs or shorts alone, so the two columns add up to "All".',
  // Not in the audit. GET /api/track-record/summary closed_by_symbol
  // (services/signal_summary_service.py _symbol_records).
  assetSplit:
    'The same closed signals, grouped by coin, each computed exactly as the headline is. Coins move together, so a few closes of one coin say little on their own: every row shows its own count and interval, and no rate is quoted below 5 closes.',
  // Not in the audit. time_in_trade (services/signal_summary_service.py _time_in_trade).
  timeInTrade:
    'How long closed signals were open, from publication to close. The median is the typical hold; the mean is pulled up by the few that ran to expiry.',
  // Not in the audit. utils/winRate.ts SMALL_SAMPLE_SIZE.
  smallSample:
    'Fewer than 30 closes. The rate is real, but on a sample this size its 95% interval is still wide - read the interval, not just the percentage.',
  profitFactor: 'Gross profit divided by gross loss. Above 1.0 means winners outweigh losers; 2.0+ is strong.',
  avgRR: 'Average reward-to-risk across logged trades — how much you made per unit risked, actual rather than planned.',
  // Not in the audit. Planned, not realised: backend
  // services/creator_profile_service.py averages |take_profit - entry| /
  // |entry - stop_loss| over the creator's signals.
  avgSignalRR:
    "Average planned reward-to-risk across this creator's signals: the distance to take-profit divided by the distance to the stop.",
  logTrade:
    'Record a trade you took in your personal journal on My Profile. It is self-reported, so it never counts toward the Track Record.',
  // Manual: services/track_record_service.py (trade records). Automated:
  // GET /api/signals/equity-curve and services/signal_summary_service.py
  // (closed real signals).
  twoTrackRecords:
    "The Track Record page and the Dashboard's Track record panel are both automated: every real signal the engine opened and closed, losses in full, with no manual trades and no demo or paper signals. The personal journal on My Profile holds trades you log yourself with Log Trade; it is self-reported, so it is never part of the Track Record.",
  // pages/TrackRecord.tsx: means of profit_loss_pct over wins / losses.
  avgWin:
    'Average price move on the signals that closed in profit, before trading fees.',
  avgLoss:
    'Average price move on the signals that closed at a loss, before trading fees. Losses count in full.',
  // pages/FeeCalculator: the reader’s fee, sent as ?fee_rate= and netted by
  // the backend (services/signal_summary_service.fee_calculator_record).
  feeSlider:
    'Your trading fee, percent of the trade per side, charged on entry and again on exit. Fee only: slippage is not included. The presets are Kraken’s published fees by tier and order type (taker for market orders, maker for limit orders), checked Oct 9, 2026 — confirm current fees at kraken.com; any other value from 0% to 5% can be typed in. Moving it is a what-if: if you are signed in, your saved fee changes only when you press Save.',
  grossRecord:
    'The signals’ own record, called on the price move before any trading fee: win rate, wins and losses, average win and loss, average R and the summed result. It measures the signals, not an account. Fees depend on your exchange and tier, so none is assumed; the fee calculator applies yours.',

  // Not in the audit. The headline relabelled on 2026-09-20: total_roi_pct /
  // sum_of_returns_pct is sum(per-trade %), which troughs below -100% while
  // the account is intact.
  sumOfTradeReturns:
    'Each closed trade\u2019s percentage return added together - unsized, not compounded, and not an account return. Ten trades at −20% read as −200% here, while an account risking a fixed slice per trade would still be standing. It was labelled cumulative P&L once, which made it look like a balance; it never was one. Judge the record on average R and on the win rate — each read with the interval under it.',

  // Not in the audit. The banner on both record views, and the engine's
  // standing evaluation status (SIGNALS_GLOSSARY.engineStatus).
  recordHonesty:
    'Every closed signal is listed on the Track Record: wins, losses and expiries closed at the market price. A signal can be excluded from the figures only for a stated reason, such as an entry price that was already stale when the signal opened. The same rule applies to wins and losses, and excluded signals stay listed in their own section with the result they recorded and why. Nothing is deleted or hidden. The engine is under evaluation and has not been shown to have an edge, so the record is published to show that honestly, not to advertise a result.',
  maxDrawdown:
    'The largest fall from a high point of the curve to a later low, in percentage points, before fees. Shows how deep the worst losing stretch went.',
  // Paper view: max of PaperEquitySnapshot.drawdown_pct (backend paper_engine).
  maxDrawdownPeak:
    'The largest fall in account value from a previous high, as a percent of that high. The forward test is stopped if it reaches 35%.',
  avgR:
    'Average realised result per closed trade, in units of the risk it was opened with. +1R made what the stop risked; −1R lost it. The signal engine’s figure is before fees, like every engine result shown; the paper account’s is after its own simulated fee. Each states its basis under the figure.',
  interval:
    'The range the true average is likely to sit in, at 95% confidence, given how many trades are behind it and how much they varied. An interval that includes 0 means the record cannot yet tell an edge from luck — which is not the same as having no edge. Below two closes there is no spread to measure and the figure is shown as not measured.',
  // Fee calculator (pages/FeeCalculator), netted by the backend at the fee
  // entered: services/signal_summary_service.fee_calculator_record.
  rAfterYourFee:
    'A signal’s R after your fee: its gross R less a round trip at your fee (charged on entry and again on exit) measured against its own stop distance, so a tight stop pays more of its risk in fees than a wide one. Fee only: slippage is not included.',
  totalAfterYourFee:
    'Every closed signal’s R after your fee, added up. Signals with no usable stop distance have no R and are left out, and counted separately.',
  winsAfterFees:
    'Closed signals whose result is still above zero after a round trip at your fee. The record’s own wins and losses never change: they are called on the gross move. This is only what your fee would leave.',
  // pages/TrackRecord Paper view: means of pnl_pct, which the backend nets of both fees.
  paperAvgWin: 'Average return on the paper trades that made money, after the 0.25% fee on entry and on exit.',
  paperAvgLoss: 'Average return on the paper trades that lost money, after fees. A trade that only covered its fees counts as a loss.',
  paperReturn: DASHBOARD_GLOSSARY.paperReturn,
}

// ---- Practice (margin simulator) ----

// Not in the audit. Frontend pages/Practice.tsx and backend
// services/practice_service.py: a virtual wallet on live Kraken prices with
// isolated-margin mechanics and a six-step tutorial. Admin-gated today, behind
// the same check as live trading. It calls no order, trade or execution
// endpoint, and its tables hold no foreign key into trading_signals.
export const PRACTICE_GLOSSARY = {
  practiceSimulator:
    "A margin trading simulator at /practice: a virtual $10,000 wallet on real, live Kraken prices, there to teach long, short, leverage and liquidation on money that isn't real. It places no orders anywhere and moves no funds, and its results never touch the signal record, the track record or any win rate. Every screen carries a NOT REAL MONEY badge.",

  virtualWallet:
    'The practice wallet starts at $10,000 of virtual USD and can be reset back to $10,000 whenever you like. It is not a balance, not withdrawable and not connected to any exchange account.',

  leverage:
    "Borrowing so a position is larger than the collateral behind it: at 5×, $200 of collateral controls $1,000 of the asset. Leverage multiplies gains and losses alike, and a leveraged position can lose more than you put into it - at 5× a 20% move against you takes the whole collateral. The higher the leverage, the closer the liquidation price sits to your entry.",

  liquidation:
    'The forced close that fires when price reaches the level where the collateral is used up: below entry for a long, above it for a short. It takes the entire collateral and ends the position, so there is no holding on for a recovery. At 1× there is no liquidation at all - the worst case is the stop. The simulator shows the liquidation price live, with a bar for how close price has come to it.',

  isolatedMargin:
    'Each practice position carries its own collateral, so a liquidation costs that position its collateral and nothing else in the wallet.',

  shortPosition:
    "The mirror image of a long: you sell or borrow the asset, so the position gains when price FALLS and loses when it rises. The P&L is inverted, and since there is no ceiling on a price there is no fixed worst case the way there is on a long. FinCreator does not execute shorts - the simulator is for learning the mechanics.",

  holdingCost:
    'A leveraged position is not free to hold: a taker fee is charged going in and again coming out, and borrow interest accrues on the borrowed portion for as long as the position stays open. The simulator charges both, the way an exchange would, so a position that goes nowhere still drains.',

  tutorial:
    'A six-step guided walkthrough: this is fake money, place a long, place a short, get liquidated on purpose, leverage versus risk, and fees and interest. It teaches the mechanics and takes no view on whether anyone should trade on margin. Completing it is recorded but unlocks nothing today.',

  notRealTrading:
    'The simulator is a teaching tool, not trading. FinCreator does not execute margin trades: if you decide to trade on margin for real, that happens on your own exchange account, with your own money, at your own risk - and real money can be lost in full. Learn the mechanics here first.',
}

// ---- Monetization ----

export const MONETIZATION_GLOSSARY = {
  mrr: 'Monthly recurring revenue — predictable subscription income, excluding one-off tips.',
  arpu: 'Average revenue per user. Total revenue divided by subscriber count.',
  availableBalance: 'Cleared earnings you can withdraw now.',
  pendingBalance: "Earnings still in the payment processor's hold period, not yet withdrawable.",
  periodRevenue: 'Income earned inside the selected date window, versus the window before it.',
  lifetimeEarnings: 'Everything earned since account creation, before platform fees and payouts.',
  tipRevenue: 'One-off payments from followers, separate from recurring subscriptions.',
  netGrowth: 'New subscribers minus cancellations for the period. Negative means shrinkage.',
  // Not in the audit.
  tiers: 'Subscription levels you define, each with its own price and benefits. Subscribers pick one to access your paid content.',
  // Not in the audit.
  payouts:
    'Transfers of your available balance to your bank account through Stripe Connect. Pending earnings are not included until they clear.',
}

// ---- Analytics ----

export const ANALYTICS_GLOSSARY = {
  winRateClosed: 'Share of completed trades that were profitable. Open positions are excluded entirely.',
  totalPL: 'Profit and loss across closed signals, in dollars.',
  live: 'Data is streaming in real time rather than showing a cached snapshot.',
  winningSignals: 'Count of finished trades that hit target before stop.',
  engagement: (rate: number) =>
    'Interactions divided by impressions on published posts.' +
    (rate === 0 ? ' Zero here means nothing has been published yet.' : ''),
}

// ---- Alerts ----

export const ALERTS_GLOSSARY = {
  frequencyOnce: 'Fires a single time then deactivates, rather than firing again on every check while the level holds.',
  threshold: 'What triggers the alert once it is met: a price for Price Above (at or above it) and Price Below (at or below it); a percentage move for % Rise and % Drop, measured from the price at the alert\'s first check.',
  triggeredCount: 'How often this alert has fired since creation.',
}

// ---- Settings ----

export const SETTINGS_GLOSSARY = {
  creatorVerification:
    'A self-attested application, reviewed by the FinCreator team, that unlocks paid features. Currently US-based creators only.',
  applyToMonetize:
    'Required before subscription tiers, marketplace products or signal packages become available. Free posting works without it.',
  whiteLabel:
    "Replaces the FinCreator shell with your own logo, colors and domain, so subscribers see your storefront rather than the platform's.",
  customDomain: 'Serve your creator page from a domain you own instead of a FinCreator subdomain.',
  personalization:
    'Lets the platform use your activity to tailor recommendations. Off means recommendations stay generic.',
  analyticsCookies: 'Shares anonymized usage data for product improvement. No effect on your trading data.',
  exportData: 'Downloads everything tied to your account — profile, portfolios, subscriptions, payments and signals.',
  deleteAccount: 'Submits a deletion request for review. It is not immediate and not self-service.',
}

// ---- Profile ----

export const PROFILE_GLOSSARY = {
  // Backend api/community.py, _creator_total_return: sum of profit_loss_percent
  // over the creator's closed, non-deleted signals; null until one closes.
  totalReturn:
    "Sum of the percentage returns on your closed signals — added together, not compounded, and unsized, so it is a tally rather than an account return. Open signals aren't included. Total ROI in your personal journal is the same measure over trades you log yourself.",
  // Backend services/track_record_service.py: total_roi_pct is the sum of
  // profit_loss_pct over closed trade records entered in the period.
  totalRoi:
    'Sum of the percentage returns on your closed logged trades in this period — added together, not compounded, and unsized, so it is not an account return.',
  currentStreak: 'Consecutive wins or losses in your most recent trades. A dash means no trades logged yet.',
  winLossCount: 'Win and loss counts behind the win-rate percentage.',
  periodFilter:
    'Scopes the stats below to a window — All Time through This Week. Shorter windows mean smaller, noisier samples.',
}

// ---- A/B Testing & Live Trading Room ----

export const AB_TESTING_GLOSSARY = {
  abTest:
    'Publishes two versions of the same idea and measures which earns more engagement, so the choice is evidence-based rather than a guess.',
  variants: 'The competing versions under test — different hooks, captions or formats for one trade idea.',
  // Backend services/ab_testing_service.py (auto_select_winner_if_ready;
  // evaluate_significance: two-proportion z-test per view, Bonferroni-adjusted
  // across challengers; significance_threshold defaults to 0.05) and
  // tasks/ab_testing_tasks.evaluate_running_ab_tests, every 1800s.
  winnerRule:
    "With auto-select on, the test is checked every 30 minutes and ends once every variant has its minimum sample of views. The variant with the best rate on the test's metric (engagement rate, likes, comments or shares per view) wins if it is the control or beats the control at the test's significance threshold (5% by default); there is no fixed duration.",
}

export const LIVE_ROOM_GLOSSARY = {
  session: 'A scheduled live video broadcast your audience can join in real time.',
}

// ---- Feed, Marketplace, Leaderboard & Content ----

export const FEED_GLOSSARY = {
  tabs: "Following shows accounts you follow, the FinCreator Engine's signals and your own posts; Discover shows accounts you don't follow yet; Trending ranks the last 7 days by likes and comments, newer engagement counting for more.",
}

export const MARKETPLACE_GLOSSARY = {
  signalPackages:
    "What creators sell here: live calls by subscription, or a strategy or course package. A package never shows a win rate of its own (a set of signals someone picked isn't their record); each links to the creator's full record.",
}

export const LEADERBOARD_GLOSSARY = {
  // Backend services/community_hub_service.py, _batch_calculate_leaderboard_scores
  // and _score_signal_win_rate / _score_subscriber_growth / _score_engagement.
  // Only those three types are scored; OVERALL_PERFORMANCE, REVENUE,
  // CONTENT_QUALITY, CONSISTENCY and CUSTOM boards give every creator 0.
  ranking: (leaderboardType?: string) =>
    ({
      SIGNAL_WIN_RATE:
        "Ranked by win rate: the share of each creator's signals from this period that closed as wins.",
      SUBSCRIBER_GROWTH: 'Ranked by subscriber growth: the percent change in active subscribers over this period.',
      ENGAGEMENT:
        'Ranked by engagement points on content created this period: 1 per like, 2 per comment, 3 per share.',
    } as Record<string, string>)[leaderboardType ?? ''] ??
    'Each board ranks creators by its own score: signal win rate, subscriber growth rate, or engagement points (likes, comments ×2, shares ×3).',
}

export const CONTENT_GLOSSARY = {
  repurpose: "Rewrites this post for a different platform's format and length, reusing the same underlying trade idea.",

  // Not in the audit. Backend services/content_grounding.py.
  grounding:
    "Generated posts may only state verified figures: the signal's own levels and outcome, your trade plan labelled as yours, live Kraken prices for tracked coins, and crypto headlines from the last 24 hours attributed to the outlet that reported them. A price that can't be fetched is left out, never replaced with a made-up one.",

  // Not in the audit. content_grounding.py _NO_DATA_TOPICS, OutOfScopeContentError
  // (422) and NewsUnavailableError (503).
  groundingRefusals:
    "Requests FinCreator has no live data for are refused rather than written from the AI's memory: gold, silver, oil and other commodities, stocks and stock indices, forex, bonds, macroeconomic data (Fed, rates, CPI, jobs), real estate, elections, general news, and trade plans for coins outside the tracked universe. A crypto news post is also refused when no headlines from the last 24 hours are stored; try again later or write about a coin's price or a signal.",

  // Not in the audit. Backend core/config.py FREE_TIER_MONTHLY_GENERATIONS and
  // services/ai_usage_service.py (per calendar month, UTC).
  aiQuota:
    'Each account can run 100 AI content generations per calendar month (UTC). A Create run spends one per platform selected. Past the limit, generation is refused until the month rolls over. The in-app assistant has its own daily question limit and does not use this quota.',
}

// ---- Dashboard views & paper forward test ----

// Not in the audit. Frontend pages/Dashboard.tsx VIEW_LABELS; backend
// tasks/celery_app.py (hourly scan) and PAPER_STRATEGY_SPEC.md; paper
// constants in backend models/paper.py.
export const PAPER_GLOSSARY = {
  viewToggle:
    'The Dashboard has two views. Signal engine is the three-phase signal engine and its real record. Paper shows a separate forward test run with pretend money on real prices. Paper never counts toward the real record or win rate, and the two are never mixed with each other.',

  signalEngine:
    "The three-phase signal engine. It scans every tracked pair live each hour and opens signals when its risk checks allow. A signal ends one of three ways: it reaches its take profit (a win), it hits its stop loss (a loss), or it expires - and an expired signal is closed at the market price and scored like any other, not dropped. Its record shows every closed signal, losses included. The engine is under evaluation and has no proven edge.",

  paperStrategy:
    'A frozen, rules-only strategy (breakout-blend-4) forward-tested on live data for 180 days from launch, starting with $10,000 of pretend money. It runs once a day at 00:15 UTC on the closed Kraken daily candle and fills at the next daily open. Long only: no shorting, no leverage. Rules never change mid-test.',

  regimeFilter:
    "The paper strategy only trades while BTC's daily close is above its 200-day average. When that switches off, every paper position exits at the next open and the account waits in cash.",

  breakoutEntry:
    "Four sub-books watch 20, 30, 40 and 55-day windows. A coin is bought when its daily close beats every close in that window. A big breakout fires several windows at once, so one coin can be held up to four times, once per sub-book. Open positions shows it as one row, with a tag for each window holding it.",

  trailingStop:
    "There is no take-profit. Each paper position trails a stop 3 × ATR(14) below its highest daily close since entry - ATR being the coin's typical daily move, so three of them is a fall well outside its normal range rather than ordinary noise. The stop rises as the high rises and never moves down, so it locks in more of the gain the further a winner runs. The position exits at the next open after a daily close below the stop; intraday wicks don't count. Paper open positions shows each position's ATR at entry, its current trailing stop and how far price sits above it.",

  paperSizing:
    "Equal weight, as in the backtest: each sub-book gets a quarter of equity, split evenly across the coins eligible that day, with the 0.25% fee per side taken from that slot. One coin held by all four sub-books is at most about 2% of the account. Never more than 100% of equity invested.",

  killRule:
    'Set before launch and applied without discretion. The test stops if the account is below $10,000 once 100 trades have closed, or if equity falls 35% from its peak. It then closes everything at the next open and freezes the ledger. A kill is a result, not a malfunction.',

  paperWinRate:
    'The strategy is built to win only 30–40% of trades and rely on a few large winners. A low win rate or a run of losses is expected and is not evidence either way.',
}
